import React, { useState } from "react";
import Chart from "react-apexcharts";
import { motion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";

import DataStatisticsTable from "components/Table/DataStatisticsTable";
import CampaignModal from "components/Modal/CampaignModal";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import type { LucideIcon } from "lucide-react";

interface StatCardData {
  title: string;
  value: string;
  /** Modern token-based styling (set by Dashboard/Index). */
  bg?: string;
  accent?: string;
  border?: string;
  icon?: LucideIcon;
  /** Legacy props — kept so callers passing old shape still render. */
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
}

interface ChartLegendItem {
  color: string;
  label: string;
  value: string;
  count: number;
}

interface TableHeader {
  key: string;
  label: string;
}

interface LeadEngagementCardProps {
  chartOptions: any;
  chartSeries: number[];
  statCards: StatCardData[];
  chartLegends: ChartLegendItem[];
  tableHeaders: TableHeader[];
  tableData: Record<string, any>[];
  title?: string;
}

const LeadEngagementCard: React.FC<LeadEngagementCardProps> = ({
  chartOptions,
  chartSeries,
  statCards,
  chartLegends,
  tableHeaders,
  tableData,
  title = "Lead Engagement",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const displayedData = tableData.slice(0, 3);
  const hasData =
    chartSeries && chartSeries.length > 0 && chartSeries.some((v) => v > 0);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border/60">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {/* Chart */}
          <div className="order-2 lg:order-1">
            <div className="mb-4">
              {hasData ? (
                <Chart
                  options={{
                    ...chartOptions,
                    chart: {
                      ...chartOptions.chart,
                      height: window.innerWidth < 640 ? 250 : 300,
                    },
                  }}
                  series={chartSeries}
                  type="donut"
                  height={window.innerWidth < 640 ? 250 : 300}
                />
              ) : (
                <div className="flex h-64 items-center justify-center sm:h-72">
                  <div className="text-center">
                    <Info className="mx-auto mb-3 h-12 w-12 text-muted-foreground/60" />
                    <p className="mb-1 text-sm font-medium text-muted-foreground sm:text-base">
                      No Data Available
                    </p>
                    <p className="text-xs text-muted-foreground/80 sm:text-sm">
                      There are no email or text message statistics to display.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {hasData && (
              <>
                <div className="mb-4 flex items-center justify-center gap-3">
                  {chartLegends.map((legend, index) => (
                    <div key={index} className="flex items-center">
                      <span
                        className={cn(
                          "mr-2 h-2.5 w-2.5 rounded-full",
                          legend.color
                        )}
                      />
                      <span
                        className={cn(
                          "text-xs font-medium sm:text-sm",
                          legend.label
                        )}
                      >
                        {legend.value}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-center text-xs font-medium text-muted-foreground sm:text-sm">
                  Channel Breakdown
                </p>
              </>
            )}
          </div>

          {/* Stats + table */}
          <div className="order-1 lg:order-2 lg:col-span-2">
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
              {statCards.map((card, index) => {
                const Icon = card.icon;
                const bgClass = card.bg ?? card.bgColor;
                const borderClass = card.border ?? card.borderColor;
                const accentClass = card.accent ?? card.textColor;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.06 }}
                    className={cn(
                      "rounded-xl border p-4 sm:p-5",
                      bgClass,
                      borderClass
                    )}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {card.title}
                      </p>
                      {Icon ? (
                        <span
                          className={cn(
                            "grid h-7 w-7 place-items-center rounded-lg bg-card/60",
                            accentClass
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                      ) : null}
                    </div>
                    <p
                      className={cn(
                        "text-2xl font-semibold tracking-tight sm:text-3xl",
                        accentClass
                      )}
                    >
                      {card.value}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between sm:mb-4">
                <h3 className="text-sm font-semibold sm:text-base">
                  Top Performing Campaigns
                </h3>
                {tableData.length > 3 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsModalOpen(true)}
                  >
                    View all {tableData.length}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div
                className="cursor-pointer overflow-hidden rounded-xl border border-border/60 bg-card transition-colors hover:bg-muted/30"
                onClick={() => setIsModalOpen(true)}
                title="Click to view all campaigns"
              >
                <DataStatisticsTable
                  headers={tableHeaders}
                  data={displayedData}
                />
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      <CampaignModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tableHeaders={tableHeaders}
        tableData={tableData}
        title="All Top Performing Campaigns"
      />
    </Card>
  );
};

export default LeadEngagementCard;
